import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Search, MapPin, Filter, ChevronDown, ChevronUp, QrCode, AlertTriangle } from 'lucide-react';
import Table, { ActionButton, ActionMenu } from '../../components/common/Table';
import Modal, { ConfirmModal, FormModal } from '../../components/common/Modal';
import ReportForm from '../../components/forms/ReportForm';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const SitesPage = () => {
  const { user: currentUser } = useAuth();
  const [sites, setSites] = useState([]);
  const [farms, setFarms] = useState([]);
  const [farmDetails, setFarmDetails] = useState({}); // Cache for individual farm details
  const [userFarms, setUserFarms] = useState([]); // Current user's associated farms
  const [userFarmsLoaded, setUserFarmsLoaded] = useState(false); // Flag to track if user farms have been loaded
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    watershed_id: '',
  });

  // Modal states
  const [selectedSite, setSelectedPlot] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Report modal data
  const [reportPlotData, setReportPlotData] = useState(null);

  // Filter collapse state
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  // QR Code generation state
  const [qrCodeGenerating, setQrCodeGenerating] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    watershed_id: '',
    coordinates: {
      latitude: 0,
      longitude: 0,
    },
    elevation: '',
    flow_regime: '',
    waterbody_type: '',
    managing_organization: '',
    contact: '',
    website: '',
  });

  const columns = [
    {
      key: 'name',
      title: 'Site Name',
      sortable: true,
      render: (site) => (
        <div className="flex items-center">
          <MapPin className="w-4 h-4 text-primary-600 mr-2" />
          <div>
            <div className="font-medium text-secondary-900">{site.name}</div>
            <div className="text-sm text-secondary-500">{site.description}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'farm_name',
      title: 'Farm',
      sortable: true,
      className: 'hidden md:table-cell',
      render: (site) => {
        const farm = farmDetails[site.watershed_id];
        return (
          <div>
            <div className="font-medium text-secondary-900">{farm?.name || 'Loading...'}</div>
            <div className="text-sm text-secondary-500">{farm?.address || farm?.region || ''}</div>
          </div>
        );
      },
    },
    {
      key: 'coordinates',
      title: 'Location',
      className: 'hidden lg:table-cell',
      render: (site) => (
        <div className="text-sm">
          <div>Lat: {site.coordinates?.latitude || 0}</div>
          <div>Lng: {site.coordinates?.longitude || 0}</div>
        </div>
      ),
    },
    {
      key: 'area',
      title: 'Area',
      sortable: true,
      className: 'hidden md:table-cell',
      render: (site) => (
        <span className="text-secondary-900">
          {site.area} {'sq ft'}
        </span>
      ),
    },
    {
      key: 'qr_code',
      title: 'QR Code',
      className: 'hidden lg:table-cell',
      render: (site) => (
        site.qr_code ? (
          <div className="flex items-center">
            <QrCode className="w-4 h-4 text-primary-600 mr-1" />
            <a
              href={site.qr_code}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 text-sm"
            >
              View QR
            </a>
          </div>
        ) : (
          <button
            onClick={() => handleGenerateQRCode(site.id)}
            disabled={qrCodeGenerating[site.id]}
            className="btn btn-sm btn-secondary text-xs px-2 py-1 disabled:opacity-50"
          >
            {qrCodeGenerating[site.id] ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600 mr-1"></div>
                Generating...
              </>
            ) : (
              <>
                <QrCode className="w-3 h-3 mr-1" />
                Generate QR
              </>
            )}
          </button>
        )
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (site) => new Date(site.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '120px',
      render: (site) => (
        <div className="flex space-x-2">
          <ActionButton
            icon={Eye}
            label="View Details"
            onClick={() => handleViewPlot(site)}
          />
          <ActionMenu
            item={site}
            actions={[
              {
                key: 'report',
                label: 'Submit Report',
                icon: AlertTriangle,
                variant: 'warning',
                onClick: handleReportPlot,
              },
              {
                key: 'edit',
                label: 'Edit Site',
                icon: Edit,
                onClick: handleEditPlot,
              },
              {
                key: 'delete',
                label: 'Delete Site',
                icon: Trash2,
                variant: 'danger',
                onClick: handleDeletePlot,
              },
            ]}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadUserFarms();
  }, [currentUser]);

  useEffect(() => {
    // Only load sites and farms data after user farms have been determined
    if (userFarmsLoaded) {
      loadPlots();
      loadFarms();
    }
  }, [filters, userFarms, userFarmsLoaded]);

  const loadPlots = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // For single farm filter
      if (filters.watershed_id) {
        params.watershed_id = filters.watershed_id;
      }
      
      // For watershed_manager users, pass their assigned farms as comma-separated watershed_ids
      if (currentUser?.role === 'watershed_manager' && userFarms.length > 0) {
        params.watershed_ids = userFarms.join(',');
      }

      console.log({
        "filters": filters,
        "params": params,
        "userFarms": userFarms
      });

      const response = await apiService.getSites(params);
      
      if (response.success) {
        let plotsData = response.data.sites || [];
        
        // Filter by search term if provided
        if (filters.search.trim()) {
          plotsData = plotsData.filter(site => 
            site.name.toLowerCase().includes(filters.search.toLowerCase()) ||
            site.description.toLowerCase().includes(filters.search.toLowerCase())
          );
        }
        
        setSites(plotsData);
        
        // Fetch individual farm details for each unique watershed_id
        const uniqueFarmIds = [...new Set(plotsData.map(site => site.watershed_id))];
        await loadFarmDetails(uniqueFarmIds);
      } else {
        toast.error(response.message || 'Failed to load sites');
      }
    } catch (error) {
      console.error('Error loading sites:', error);
      toast.error('Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  const loadFarms = async () => {
    try {
      const response = await apiService.getWatersheds({ limit: 1000 });
      if (response.success) {
        let farmsData = response.data.watersheds || [];
        
        // Filter farms for watershed_manager users - only show their assigned farms
        if (currentUser?.role === 'watershed_manager' && userFarms.length > 0) {
          farmsData = farmsData.filter(farm => 
            userFarms.includes(farm.id)
          );
        }
        
        setFarms(farmsData);
      }
    } catch (error) {
      console.error('Error loading farms:', error);
    }
  };

  const loadFarmDetails = async (farmIds) => {
    try {
      const farmPromises = farmIds.map(async (farmId) => {
        // Skip if we already have this farm's details
        if (farmDetails[farmId]) return null;
        
        try {
          const response = await apiService.getWatershed(farmId);
          if (response.success) {
            return { id: farmId, data: response.data };
          }
        } catch (error) {
          console.error(`Error loading farm ${farmId}:`, error);
          return { id: farmId, data: { name: 'Unknown Farm' } };
        }
        return null;
      });
      
      const farmResults = await Promise.all(farmPromises);
      const newFarmDetails = { ...farmDetails };
      
      farmResults.forEach(result => {
        if (result) {
          newFarmDetails[result.id] = result.data;
        }
      });
      
      setFarmDetails(newFarmDetails);
    } catch (error) {
      console.error('Error loading farm details:', error);
    }
  };

  const loadUserFarms = async () => {
    try {
      setUserFarmsLoaded(false); // Reset the flag
      
      // If user has farms in their profile, use those
      if (currentUser?.farms && Array.isArray(currentUser.farms)) {
        console.log('Setting user farms from currentUser.farms:', currentUser.farms);
        setUserFarms(currentUser.farms);
        setUserFarmsLoaded(true);
        return;
      }
      
      // For watershed_manager role, try to fetch user details to get farm associations
      if (currentUser?.role === 'watershed_manager') {
        try {
          const response = await apiService.getCurrentUserDetails();
          if (response.success && response.data?.farms) {
            console.log('Setting user farms from API response:', response.data.farms);
            setUserFarms(response.data.farms);
          } else {
            console.log('No farms found in API response');
            setUserFarms([]);
          }
        } catch (error) {
          console.error('Error loading user farm associations:', error);
          // If we can't load user details, don't filter (show all)
          setUserFarms([]);
        }
      } else {
        console.log('User is not watershed_manager, clearing userFarms');
        setUserFarms([]);
      }
      
      setUserFarmsLoaded(true); // Mark as loaded regardless of the result
    } catch (error) {
      console.error('Error in loadUserFarms:', error);
      setUserFarms([]);
      setUserFarmsLoaded(true); // Mark as loaded even on error
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      watershed_id: '',
      coordinates: {
        latitude: 0,
        longitude: 0,
      },
      elevation: '',
      flow_regime: '',
      waterbody_type: '',
      managing_organization: '',
      contact: '',
      website: '',
    });
  };

  const handleViewPlot = (site) => {
    setSelectedPlot(site);
    setShowViewModal(true);
  };

  const handleCreatePlot = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleEditPlot = (site) => {
    setSelectedPlot(site);
    setFormData({
      name: site.name,
      description: site.description || '',
      watershed_id: site.watershed_id,
      coordinates: {
        latitude: site.coordinates?.latitude || 0,
        longitude: site.coordinates?.longitude || 0,
      },
      elevation: site.elevation?.toString() || '',
      flow_regime: site.flow_regime || '',
      waterbody_type: site.waterbody_type || '',
      managing_organization: site.managing_organization || '',
      contact: site.contact || '',
      website: site.website || '',
    });
    setShowEditModal(true);
  };

  const handleDeletePlot = (site) => {
    setSelectedPlot(site);
    setShowDeleteModal(true);
  };

  const handleReportPlot = async (site) => {
    try {
      // Get the full site data
      const plotResponse = await apiService.getSite(site.id);
      if (!plotResponse.success) {
        toast.error('Failed to load site details');
        return;
      }

      // Get the farm data
      const farmResponse = await apiService.getWatershed(site.watershed_id);
      if (!farmResponse.success) {
        toast.error('Failed to load farm details');
        return;
      }

      // Structure the data as expected by ReportForm (site-level report)
      const plotData = {
        farm: farmResponse.data,
        site: plotResponse.data
      };

      setReportPlotData(plotData);
      setShowReportModal(true);
    } catch (error) {
      console.error('Error loading site data for report:', error);
      toast.error('Failed to load site details for reporting');
    }
  };

  const handleReportSuccess = () => {
    setShowReportModal(false);
    setReportPlotData(null);
    toast.success('Report submitted successfully');
  };

  const handleFormSubmit = async (isEdit = false) => {
    if (!formData.name.trim() || !formData.watershed_id || !formData.flow_regime || !formData.waterbody_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setFormLoading(true);

      const siteData = {
        name: formData.name,
        description: formData.description,
        watershed_id: formData.watershed_id,
        coordinates: {
          latitude: parseFloat(formData.coordinates.latitude) || 0,
          longitude: parseFloat(formData.coordinates.longitude) || 0,
        },
        elevation: parseFloat(formData.elevation) || 0,
        flow_regime: formData.flow_regime,
        waterbody_type: formData.waterbody_type,
        managing_organization: formData.managing_organization || '',
        contact: formData.contact || '',
        website: formData.website || '',
      };

      let response;
      if (isEdit && selectedSite) {
        response = await apiService.updateSite(selectedSite.id, siteData);
      } else {
        response = await apiService.createSite(siteData);
      }
      
      if (response.success) {
        toast.success(isEdit ? 'Site updated successfully' : 'Site created successfully');
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
        loadPlots();
      } else {
        toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} site`);
      }
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} site:`, error);
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} site`);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSite) return;

    try {
      const response = await apiService.deleteSite(selectedSite.id);
      
      if (response.success) {
        toast.success('Site deleted successfully');
        setShowDeleteModal(false);
        loadPlots();
      } else {
        toast.error(response.message || 'Failed to delete site');
      }
    } catch (error) {
      console.error('Error deleting site:', error);
      toast.error('Failed to delete site');
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleGenerateQRCode = async (plotId) => {
    try {
      setQrCodeGenerating(prev => ({ ...prev, [plotId]: true }));

      const response = await apiService.generateSiteQRCode(plotId);

      if (response.success) {
        toast.success('QR code generated successfully');
        loadPlots(); // Reload to get the updated site with QR code
      } else {
        toast.error(response.message || 'Failed to generate QR code');
      }
    } catch (error) {
      console.error('QR code generation error:', error);
      toast.error(error.message || 'Failed to generate QR code');
    } finally {
      setQrCodeGenerating(prev => ({ ...prev, [plotId]: false }));
    }
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const canManagePlots = currentUser && ['admin', 'super_admin', 'watershed_manager'].includes(currentUser.role);

  return (
    <div className="space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900">Sites</h1>
          <p className="text-sm md:text-base text-secondary-600">Manage farm sites and their details</p>
        </div>
        {canManagePlots && (
          <button
            onClick={handleCreatePlot}
            className="btn btn-primary flex items-center gap-2 w-full md:w-auto"
          >
            <Plus className="w-4 h-4" />
            <span className="md:inline">Create Site</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mx-2 md:mx-0">
        {/* Filter Header */}
        <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 md:w-5 md:h-5 text-secondary-500" />
            <h3 className="text-base md:text-lg font-medium text-secondary-900">Filters</h3>
          </div>
          <button
            onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
            className="flex items-center space-x-1 text-secondary-600 hover:text-secondary-900 transition-colors p-1"
          >
            <span className="text-xs md:text-sm">{isFiltersCollapsed ? 'Show' : 'Hide'}</span>
            {isFiltersCollapsed ? (
              <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
            ) : (
              <ChevronUp className="w-3 h-3 md:w-4 md:h-4" />
            )}
          </button>
        </div>

        {/* Collapsible Filter Content */}
        {!isFiltersCollapsed && (
          <div className="p-3 md:p-6">
            <div className="space-y-3 md:space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search sites by name..."
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="form-input pl-8 md:pl-10 w-full text-sm md:text-base"
                />
              </div>

              {/* Farm Filter */}
              <select
                value={filters.watershed_id}
                onChange={(e) => handleFilterChange('watershed_id', e.target.value)}
                className="form-select w-full text-sm md:text-base"
              >
                <option value="">All Farms</option>
                {farms.map((farm) => (
                  <option key={farm.id} value={farm.id}>
                    {farm.name} ({farm.location})
                  </option>
                ))}
              </select>

              <div className="flex justify-center md:justify-end">
                {/* Clear Filters */}
                <button
                  onClick={() => setFilters({ search: '', watershed_id: '' })}
                  className="btn btn-secondary text-sm md:text-base px-4 py-2"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sites Table - Desktop */}
      <div className="hidden md:flex md:flex-col md:h-[calc(100vh-200px)] md:min-h-[500px]">
        <Table
          columns={columns}
          data={sites}
          loading={loading}
          emptyMessage="No sites found"
          className="flex-1 flex flex-col"
        />
      </div>

      {/* Sites Cards - Mobile */}
      <div className="md:hidden space-y-3 px-2">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : sites.length > 0 ? (
          sites.map((site) => {
            const farm = farmDetails[site.watershed_id];
            return (
              <div key={site.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 w-full max-w-full overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between mb-3 w-full">
                  <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                    <div className="p-1.5 rounded-md flex-shrink-0 bg-primary-100 text-primary-600">
                      <MapPin className="w-3 h-3" />
                    </div>
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <div className="font-medium text-gray-900 text-xs truncate">
                        {site.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {farm?.name || 'Loading...'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    <ActionButton
                      icon={Eye}
                      label="View Details"
                      onClick={() => handleViewPlot(site)}
                      size="sm"
                      className="p-1"
                    />
                    {canManagePlots && (
                      <ActionMenu
                        item={site}
                        actions={[
                          {
                            key: 'report',
                            label: 'Submit Report',
                            icon: AlertTriangle,
                            variant: 'warning',
                            onClick: handleReportPlot,
                          },
                          {
                            key: 'edit',
                            label: 'Edit Site',
                            icon: Edit,
                            onClick: handleEditPlot,
                          },
                          {
                            key: 'delete',
                            label: 'Delete Site',
                            icon: Trash2,
                            variant: 'danger',
                            onClick: handleDeletePlot,
                          },
                        ]}
                      />
                    )}
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-2 text-xs w-full">
                  <div className="min-w-0 overflow-hidden">
                    <div className="text-gray-500 text-xs uppercase tracking-wide">Location</div>
                    <div className="font-medium text-gray-900 text-xs truncate">
                      {site.coordinates?.latitude?.toFixed(4) || 0}, {site.coordinates?.longitude?.toFixed(4) || 0}
                    </div>
                  </div>
                  <div className="min-w-0 overflow-hidden">
                    <div className="text-gray-500 text-xs uppercase tracking-wide">Area</div>
                    <div className="font-medium text-gray-900 text-xs truncate">
                      {site.area} sq ft
                    </div>
                  </div>
                </div>

                {/* Description */}
                {site.description && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500 truncate">
                      {site.description}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-center mx-2">
            <MapPin className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No sites found</p>
          </div>
        )}
      </div>

      {/* View Site Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Site Details"
        size="lg"
      >
        {selectedSite && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700">Site Name</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedSite.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Farm</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {farmDetails[selectedSite.watershed_id]?.name || 'Loading...'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Latitude</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedSite.coordinates?.latitude || 0}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Longitude</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedSite.coordinates?.longitude || 0}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Area</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedSite.area} square feet</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">QR Code</label>
                {selectedSite.qr_code ? (
                  <a
                    href={selectedSite.qr_code}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    View QR Code
                  </a>
                ) : (
                  <div className="mt-1">
                    <p className="text-sm text-secondary-500 mb-2">No QR code generated</p>
                    <button
                      onClick={() => handleGenerateQRCode(selectedSite.id)}
                      disabled={qrCodeGenerating[selectedSite.id]}
                      className="btn btn-sm btn-primary disabled:opacity-50"
                    >
                      {qrCodeGenerating[selectedSite.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <QrCode className="w-4 h-4 mr-2" />
                          Generate QR Code
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Created</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {new Date(selectedSite.created_at).toLocaleString()}
                </p>
              </div>
            </div>
            {selectedSite.description && (
              <div>
                <label className="block text-sm font-medium text-secondary-700">Description</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedSite.description}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Create Site Modal */}
      <FormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={() => handleFormSubmit(false)}
        title="Create Site"
        loading={formLoading}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Site Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="form-input"
              placeholder="Enter site name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Watershed *
            </label>
            <select
              value={formData.watershed_id}
              onChange={(e) => handleInputChange('watershed_id', e.target.value)}
              className="form-select"
              required
            >
              <option value="">Select a watershed</option>
              {farms.map((farm) => (
                <option key={farm.id} value={farm.id}>
                  {farm.name} - {farm.state}, {farm.country}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Flow Regime *
              </label>
              <select
                value={formData.flow_regime}
                onChange={(e) => handleInputChange('flow_regime', e.target.value)}
                className="form-select"
                required
              >
                <option value="">Select flow regime</option>
                <option value="Perennial">Perennial (Year-round flow)</option>
                <option value="Seasonal">Seasonal</option>
                <option value="Ephemeral">Ephemeral (Temporary)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Waterbody Type *
              </label>
              <select
                value={formData.waterbody_type}
                onChange={(e) => handleInputChange('waterbody_type', e.target.value)}
                className="form-select"
                required
              >
                <option value="">Select type</option>
                <option value="Stream">Stream</option>
                <option value="Pond">Pond</option>
                <option value="River">River</option>
                <option value="Lake">Lake</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="form-input"
              rows="3"
              placeholder="Enter site description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Latitude
              </label>
              <input
                type="number"
                step="any"
                value={formData.coordinates.latitude}
                onChange={(e) => handleInputChange('coordinates.latitude', e.target.value)}
                className="form-input"
                placeholder="0.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Longitude
              </label>
              <input
                type="number"
                step="any"
                value={formData.coordinates.longitude}
                onChange={(e) => handleInputChange('coordinates.longitude', e.target.value)}
                className="form-input"
                placeholder="0.0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Elevation (meters)
            </label>
            <input
              type="number"
              step="any"
              value={formData.elevation}
              onChange={(e) => handleInputChange('elevation', e.target.value)}
              className="form-input"
              placeholder="Enter elevation in meters"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Managing Organization
              </label>
              <input
                type="text"
                value={formData.managing_organization}
                onChange={(e) => handleInputChange('managing_organization', e.target.value)}
                className="form-input"
                placeholder="e.g., EPA Region 2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Contact
              </label>
              <input
                type="text"
                value={formData.contact}
                onChange={(e) => handleInputChange('contact', e.target.value)}
                className="form-input"
                placeholder="Phone or email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className="form-input"
              placeholder="https://example.com"
            />
          </div>
        </div>
      </FormModal>

      {/* Edit Site Modal */}
      <FormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={() => handleFormSubmit(true)}
        title="Edit Site"
        loading={formLoading}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Site Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="form-input"
              placeholder="Enter site name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Watershed *
            </label>
            <select
              value={formData.watershed_id}
              onChange={(e) => handleInputChange('watershed_id', e.target.value)}
              className="form-select"
              required
            >
              <option value="">Select a watershed</option>
              {farms.map((farm) => (
                <option key={farm.id} value={farm.id}>
                  {farm.name} - {farm.state}, {farm.country}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Flow Regime *
              </label>
              <select
                value={formData.flow_regime}
                onChange={(e) => handleInputChange('flow_regime', e.target.value)}
                className="form-select"
                required
              >
                <option value="">Select flow regime</option>
                <option value="Perennial">Perennial (Year-round flow)</option>
                <option value="Seasonal">Seasonal</option>
                <option value="Ephemeral">Ephemeral (Temporary)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Waterbody Type *
              </label>
              <select
                value={formData.waterbody_type}
                onChange={(e) => handleInputChange('waterbody_type', e.target.value)}
                className="form-select"
                required
              >
                <option value="">Select type</option>
                <option value="Stream">Stream</option>
                <option value="Pond">Pond</option>
                <option value="River">River</option>
                <option value="Lake">Lake</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="form-input"
              rows="3"
              placeholder="Enter site description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Latitude
              </label>
              <input
                type="number"
                step="any"
                value={formData.coordinates.latitude}
                onChange={(e) => handleInputChange('coordinates.latitude', e.target.value)}
                className="form-input"
                placeholder="0.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Longitude
              </label>
              <input
                type="number"
                step="any"
                value={formData.coordinates.longitude}
                onChange={(e) => handleInputChange('coordinates.longitude', e.target.value)}
                className="form-input"
                placeholder="0.0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Elevation (meters)
            </label>
            <input
              type="number"
              step="any"
              value={formData.elevation}
              onChange={(e) => handleInputChange('elevation', e.target.value)}
              className="form-input"
              placeholder="Enter elevation in meters"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Managing Organization
              </label>
              <input
                type="text"
                value={formData.managing_organization}
                onChange={(e) => handleInputChange('managing_organization', e.target.value)}
                className="form-input"
                placeholder="e.g., EPA Region 2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Contact
              </label>
              <input
                type="text"
                value={formData.contact}
                onChange={(e) => handleInputChange('contact', e.target.value)}
                className="form-input"
                placeholder="Phone or email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className="form-input"
              placeholder="https://example.com"
            />
          </div>
        </div>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Site"
        message={`Are you sure you want to delete "${selectedSite?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Report Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Submit Report"
        size="xl"
      >
        {reportPlotData && (
          <ReportForm
            siteData={reportPlotData}
            onSuccess={handleReportSuccess}
            onCancel={() => setShowReportModal(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default SitesPage;